<script setup>
import TD from "@/Components/Table/Element.vue";
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Label from "@/Components/Settings/Label.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import Heading from "@/Components/Settings/Heading.vue";
import Modal from "@/Components/Settings/Modal.vue";
import Button from "@/Components/Button.vue";

import { Head, useForm, Link } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { ref } from "vue";
const page_name = "Nutzerverwaltung";

defineProps({
    users: Object,
    invitations: Array,
    errors: Object,
});

const canEdit = Inertia.page.props.auth.can["edit settings"];
const canDelete = Inertia.page.props.auth.can["delete settings"];

const modalData = ref({
    show: false,
    id: Number,
});

function showDeleteModal(id) {
    modalData.value.id = id;
    modalData.value.show = true;
}

function deleteUser(id) {
    Inertia.delete(`/users/${id}`, {}, { preserveState: true, replace: true });
    modalData.value.show = false;
}

function reinvite(id) {
    Inertia.post(
        `/invitation/${id}/reinvite `,
        {},
        { preserveState: true, replace: true }
    );
}

function deleteInvite(id) {
    Inertia.delete(
        `/invites/${id}`,
        {},
        { preserveState: true, replace: true }
    );
}

const form = useForm({});
</script>

<template>
    <SettingsLayout>
        <div
            v-if="$props.errors.delete"
            class="bg-red-100 border-t-4 border-red-500 rounded-b text-red-900 px-4 py-3 shadow-md absolute top-0 right-0"
            role="alert"
        >
            <div class="flex">
                <div>
                    <p class="text-sm">
                        Administratoren können nicht gelöscht werden.
                    </p>
                </div>
            </div>
        </div>
        <Modal :show="modalData.show" @close="modalData.show = false">
            <template #title>
                <div>Warnung</div>
            </template>
            <template #message>
                <!-- {{ modalData.message }} -->
                <p>
                    Die Daten der Nutzers sowie dessen Login werden
                    unwiderruflich gelöscht.
                </p>
            </template>
            <template #actions>
                <div class="">
                    <div class="flex gap-5 justify-center mt-8">
                        <Button
                            class="mt-auto"
                            @click="
                                () => {
                                    modalData.show = false;
                                }
                            "
                            >Abbrechen</Button
                        >

                        <Button
                            type="button"
                            :disabled="isLoading"
                            class="mt-auto"
                            @click="deleteUser(modalData.id)"
                            >Löschen</Button
                        >
                    </div>
                </div>
            </template>
        </Modal>

        <Head :title="`${page_name} - Grundeinstellungen`" />

        <template #breadcrumb>{{ page_name }}</template>
        <template #controls>
            <HeaderLink href="/" type="close">Schließen</HeaderLink>
        </template>
        <Heading title="Nutzerverwaltung" />

        <p class="max-w-2xl text-sm text-oteoText mb-3">
            Bitte legen Sie hier die Nutzer/innen an, welche in Ihrer Praxis mit
            oteo arbeiten. Um die Nutzer/innen zu oteo einzuladen, klicken Sie
            bitte auf den Button "Neuen Nutzer einladen" und geben anschließend
            die Nutzerdaten ein. Der neue Nutzer/die neue Nutzerin kann sich so
            einen eigenen Account anlegen und mit oteo arbeiten.
        </p>

        <p class="max-w-2xl text-sm text-oteoText mb-9">
            Bitte beachten Sie, wenn Sie die E-Mail-Adresse des ersten Nutzers
            ändern, verändern Sie den Benutzernamen bei Ihrem Login.
        </p>

        <Button
            v-if="canEdit"
            href="/einstellungen/nutzerverwaltung/erstellen"
            class="mb-9"
        >
            Neuen Nutzer einladen
        </Button>

        <div class="flex flex-col">
            <div class="overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="overflow-auto max-h-[350px]">
                        <table class="min-w-full">
                            <thead class="bg-white border-b sticky top-0">
                                <tr>
                                    <th
                                        scope="col"
                                        class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                                    >
                                        Name
                                    </th>
                                    <th
                                        scope="col"
                                        class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                                    >
                                        Kürzel
                                    </th>
                                    <th
                                        scope="col"
                                        class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                                    >
                                        Rolle
                                    </th>
                                    <th
                                        scope="col"
                                        class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                                    >
                                        Tätigkeit
                                    </th>
                                    <th
                                        scope="col"
                                        class="text-sm font-medium text-gray-900 px-6 py-4 text-left border-x-2"
                                    >
                                        Aktion
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(user, index) in users.data"
                                    :key="user.id"
                                    class="border-b"
                                    :class="{ 'bg-gray-100': index % 2 === 0 }"
                                >
                                    <TD>{{ user.name }}</TD>
                                    <TD>{{ user.username }}</TD>
                                    <TD>{{ user.role.name }}</TD>
                                    <TD>{{ user.job }}</TD>
                                    <TD>
                                        <div
                                            class="flex items-center text-oteo-300"
                                        >
                                            <Link
                                                v-if="canEdit"
                                                class="mr-4"
                                                :href="`nutzerverwaltung/${user.id}/bearbeiten/`"
                                            >
                                                <i
                                                    class="las la-angle-right"
                                                ></i
                                                >bearbeiten
                                            </Link>
                                            <div
                                                v-if="
                                                    canDelete &&
                                                    user.is_admin == false
                                                "
                                                class="cursor-pointer"
                                                @click="
                                                    showDeleteModal(user.id)
                                                "
                                            >
                                                <i class="las la-times"></i>
                                                löschen
                                            </div>
                                        </div>
                                    </TD>
                                </tr>

                                <tr
                                    v-for="(user, index) in invitations"
                                    :key="user.id"
                                    class="border-b"
                                    :class="{ 'bg-gray-100': index % 2 === 0 }"
                                >
                                    <TD
                                        >{{
                                            user.firstname + " " + user.lastname
                                        }}
                                        <span class="opacity-75"
                                            >(eingeladen)</span
                                        ></TD
                                    >
                                    <TD> - </TD>
                                    <TD> - </TD>
                                    <TD> - </TD>
                                    <TD>
                                        <div
                                            class="flex items-center text-oteo-300"
                                        >
                                            <div
                                                v-if="canEdit"
                                                class="mr-4 cursor-pointer"
                                                @click="reinvite(user.id)"
                                            >
                                                <i
                                                    class="las la-angle-right"
                                                ></i
                                                >erneut einladen
                                            </div>
                                            <div
                                                v-if="canDelete"
                                                class="cursor-pointer"
                                                @click="deleteInvite(user.id)"
                                            >
                                                <i class="las la-times"></i>
                                                löschen
                                            </div>
                                        </div>
                                    </TD>
                                </tr>

                                <tr>
                                    <TD></TD>
                                    <TD></TD>
                                    <TD></TD>
                                    <TD></TD>
                                    <TD></TD>
                                </tr>
                            </tbody>
                        </table>
                        <div
                            class="sticky bottom-0 h-10 w-full bg-gradient-to-t from-white"
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    </SettingsLayout>
</template>
